import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

/**
 * Generic simple CRUD table used by Drivers, Trucks, Trailers, Customers.
 * fields: [{ key, label, type?, textarea? }]
 */
export default function SimpleCrud({ title, endpoint, fields, empty, testid }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => {
    api.get(`/admin/${endpoint}`).then((r) => setItems(r.data)).catch(() => {});
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      for (const f of fields) if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0;
      if (editing) await api.put(`/admin/${endpoint}/${editing.id}`, payload);
      else await api.post(`/admin/${endpoint}`, payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/${endpoint}/${id}`); load(); };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="font-mono-lt text-xs uppercase tracking-[0.3em] text-[var(--lt-accent-bright)] mb-2">// {title}</div>
          <h1 className="font-display font-black text-4xl text-white uppercase">{title}</h1>
        </div>
        <Button onClick={openNew} className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-xl h-11 font-head uppercase tracking-widest text-xs"><Plus className="h-4 w-4 mr-2" /> New</Button>
      </div>

      <div className="border border-[var(--lt-border)] bg-[var(--lt-panel)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--lt-bg)] text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-500">
            <tr>{fields.slice(0, 4).map((f) => <th key={f.key} className="text-left p-4">{f.label}</th>)}<th /></tr>
          </thead>
          <tbody className="divide-y divide-[var(--lt-border)]">
            {items.length === 0 && <tr><td colSpan={fields.length + 1} className="p-6 text-slate-500 text-center">No records yet.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} className="hover:bg-[var(--lt-panel-hover)]">
                {fields.slice(0, 4).map((f) => <td key={f.key} className="p-4 text-slate-300">{String(it[f.key] ?? "—") || "—"}</td>)}
                <td className="p-4 text-right"><Button size="icon" variant="ghost" onClick={() => openEdit(it)} className="text-slate-400 hover:text-white"><Pencil className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--lt-panel)] border-[var(--lt-border)] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display font-black text-2xl uppercase">{editing ? `Edit ${title}` : `New ${title}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.textarea ? "md:col-span-2" : ""}>
                <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-2 block">{f.label}</Label>
                {f.textarea
                  ? <Textarea rows={3} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="bg-[var(--lt-bg)] border-[var(--lt-border)] text-white rounded-xl" />
                  : <Input type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="bg-[var(--lt-bg)] border-[var(--lt-border)] text-white h-11 rounded-xl" />}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl border-[var(--lt-border)] bg-transparent text-slate-300">Cancel</Button>
            <Button onClick={save} className="rounded-xl bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white font-head uppercase tracking-widest">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
